import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Customer, CreateCustomer, UpdateCustomer } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['customers', companyId] as const;

export function useCustomers(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Customer[]>(`/companies/${companyId}/customers?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useCustomer(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['customers', companyId, id],
    queryFn: () => api<Customer>(`/companies/${companyId}/customers/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateCustomer(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateCustomer, 'companyId'>) =>
      apiJson<typeof input, Customer>('POST', `/companies/${companyId}/customers`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateCustomer(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomer }) =>
      apiJson<UpdateCustomer, Customer>('PATCH', `/companies/${companyId}/customers/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteCustomer(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Customer>('DELETE', `/companies/${companyId}/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
